import React, { useEffect, useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { getAliceRecommendedReferenceAttributeForRelation, getAliceRecommendedRelations, importRelationInProject } from "../../../API/AliceGPT/AliceGPT";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import Loader from "../../Shared/Loader/loader";
import { Checkbox } from "@progress/kendo-react-inputs";
import SelectedCardinality from "../Field/SelectedCardinality";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { getAllAttributes } from "../../Helpers/Schema/Schema";
import { getDatalakeImageUrl } from "../../Helpers/DateLakeStorage";
import { ENTITY_RELATION_PLURAL_LABEL } from "../../Helpers/Labels/EntityRelationLabel";
import { ATTRIBUTE_LABEL } from "../../Helpers/Labels/AttributeLabel";
import { filterRecommendedRelations } from "./utils";
import RecommendedRelationCard from "../../Shared/Recommendation/Relations/RecommendedRelationCard";
import FailedToLoadError from "../../Shared/Recommendation/Error/FailedToLoadError";
import { useTranslation } from "react-i18next";
import InnerLoader from "../../Shared/Loader/InnerLoader";
import { getAttributes } from "../../../API/Attribute/Attribute";
import { getCurrentSubscriptionForOrganisation } from "../../../API/Payment/Payment";
import { getEntitiesExceedingAttributeLimitOnCreatingRelations } from "../../Dashboard/Project/AliceGPT/utils";
import { OPENAI_MODEL } from "../../../Constants/OpenAI";

function AliceRelationRecommendationDialog({
    organizationId, projectId, entities, relations,
    onRelationsCreated, toggleDialog
}) {

    const [loading, setLoading] = useState(true);
    const [allEntities, setAllEntities] = useState([...entities]);
    const [recommendedRelations, setRecommendedRelations] = useState([]);
    const [subscription, setSubscription] = useState({});
    const [entitiesWithAttributeLimitExceed, setEntitiesWithAttributeLimitExceed] = useState([]);

    const [creatingRelations, setCreatingRelations] = useState(false);

    const [error, setError] = useState("");

    const { t } = useTranslation("linked_entity/alice_relation_recommendation_dialog");

    useEffect(() => {
        getEntities()
            .then(getRecommendedRelations)
            .then(getSubscrition)
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        const entitiesWithAttributeLimitExceeded = getEntitiesExceedingAttributeLimitOnCreatingRelations(
            allEntities,
            recommendedRelations,
            subscription
        )
        setEntitiesWithAttributeLimitExceed(entitiesWithAttributeLimitExceeded);
    }, [recommendedRelations])

    async function getEntities() {
        const entitiesWithAttributesPromises = entities.map(async entity => {
            const attributes = await getAttributes(entity._id);
            attributes.forEach(attribute => attribute.selected = !attribute.isSystemAttribute);
            return {
                ...entity,
                attributes
            }
        });
        const entitiesWithAttributes = await Promise.all(entitiesWithAttributesPromises);
        setAllEntities(entitiesWithAttributes);
    }

    async function getRecommendedRelations() {
        const entityNames = entities.map(entity => entity.name);
        const response = await getAliceRecommendedRelations(entityNames, OPENAI_MODEL);
        const filteredRelations = filterRecommendedRelations(response, relations);
        // filter one relation for each pair of entity
        const uniqueRelations = [];
        const uniqueRelationKeys = {};
        for (const relation of filteredRelations) {
            const relationKey = `${relation.from}-${relation.to}`;
            const reverseRelationKey = `${relation.to}-${relation.from}`;
            if (!uniqueRelationKeys[relationKey] && !uniqueRelationKeys[reverseRelationKey]) {
                uniqueRelationKeys[relationKey] = true;
                uniqueRelations.push(relation);
            }
        }
        setRecommendedRelations(uniqueRelations);
    }

    async function getSubscrition() {
        const subscription = await getCurrentSubscriptionForOrganisation(organizationId);
        setSubscription(subscription);
    }

    function toggleRelationChecked(relationIndex) {
        const newRecommendedRelations = [...recommendedRelations];
        newRecommendedRelations[relationIndex].checked = !newRecommendedRelations[relationIndex].checked;
        newRecommendedRelations[relationIndex].showReferenceAttribute = false;
        setRecommendedRelations(newRecommendedRelations);
    }

    function toggleShowReferenceAttribute(relationIndex) {
        setRecommendedRelations(
            relations => relations.map((relation, index) => {
                if (index === relationIndex) {
                    const recommendedAttribute = getRecommendedAttribute(relationIndex);
                    return {
                        ...relation,
                        checked: (
                            !relation.checked &&
                            !relation.showReferenceAttribute
                            ? true :
                            relation.checked
                        ),
                        showReferenceAttribute: recommendedAttribute === null ? false : !relation.showReferenceAttribute,
                        referenceAttribute: recommendedAttribute,
                        referenceAttributeLoading: false
                    }
                }
                return relation;
            })
        );
    }

    function getEntityByName(name) {
        return allEntities.find(entity => entity.name === name);
    }

    function getRecommendedAttribute(relationIndex) {
        const relation = recommendedRelations[relationIndex];

        const fromEntity = getEntityByName(relation.from);

        const fromEntityAttributesNames = fromEntity.attributes.filter(attribute => (
            !attribute.isSystemAttribute && attribute.attributeType === "String"
        )).map(attribute => attribute.attributeName);

        return fromEntityAttributesNames.length > 0 ? fromEntityAttributesNames[0] : null;
    }

    function handleReferenceAttributeChange(event, index) {
        const newRecommendedRelations = [...recommendedRelations];
        newRecommendedRelations[index].referenceAttribute = event.target.value;
        setRecommendedRelations(newRecommendedRelations);
    }

    function handleCardinalityChange(event, index) {
        setRecommendedRelations(prevValue => {
            return prevValue.map((relation, relationIndex) => {
                if (relationIndex === index) {
                    return {
                        ...relation,
                        cardinality: event.target.value.value
                    }
                }
                return relation;
            });
        })
    }

    function getSelectedAttributeNameOfEntity(fromEntityName) {
        const fromEntity = allEntities.find(entity => entity.name === fromEntityName);
        return (
            fromEntity.attributes ?
            fromEntity.attributes
                .filter(attribute => !attribute.isSystemAttribute && attribute.attributeType === "String")
                .map(attribute => attribute.attributeName)
            :
            []
        );
    }

    async function handleSubmit() {
        try {
            setCreatingRelations(true);
            const selectedRelations = recommendedRelations.filter(relation => relation.checked);
            const relations = await importRelationInProject(organizationId, projectId, selectedRelations);
            onRelationsCreated(relations);
            setCreatingRelations(false);
        } catch (e) {
            console.error(e);
        } finally {
            handleToggle();
        }
    }

    function handleToggle() {
        toggleDialog();
    }

    function isAddDisabled() {
        return (
            recommendedRelations.filter(relation => relation.checked).length === 0 ||
            recommendedRelations.some(relation => relation.showReferenceAttribute && !relation.referenceAttribute) ||
            recommendedRelations.some(relation => relation.referenceAttributeLoading) ||
            creatingRelations ||
            entitiesWithAttributeLimitExceed.length > 0
        );
    }

    function handleSelectAll() {
        setRecommendedRelations(recommendedRelations.map(relation => ({
            ...relation,
            checked: !recommendedRelations.every(relation => relation.checked)
        })));
    }

    function handleSwap(event, index) {
        event.stopPropagation();
        const newRecommendedRelations = recommendedRelations.map((relation, relationIndex) => {
            if (relationIndex === index) {
                return {
                    ...relation,
                    from: recommendedRelations[index].to,
                    to: recommendedRelations[index].from,
                    referenceAttribute: null,
                    referenceAttributeLoading: false,
                    showReferenceAttribute: false
                }
            }
            return relation;
        })
        setRecommendedRelations(newRecommendedRelations);
    }

    return (
        <Dialog
            title={(
                <label className="d-flex align-items-center">
                    <Checkbox
                        checked={recommendedRelations.every(relation => relation.checked)}
                        value={recommendedRelations.every(relation => relation.checked)}
                        onChange={handleSelectAll}
                    />&nbsp;
                    { t("title", { ns: "linked_entity/alice_relation_recommendation_dialog" }) }
                    &nbsp;
                    {
                        entitiesWithAttributeLimitExceed.length > 0 &&
                        <span className="text-danger">
                            {
                                t("max_attributes_exceeded", {
                                    limit: subscription.maxAttributesPerApp,
                                    entitiesWithAttributeLimitExceeded: entitiesWithAttributeLimitExceed.join(", "),
                                })
                            }
                        </span>
                    }
                </label>
            )}
            height={"80%"}
            width={"90%"}
            onClose={handleToggle}
        >
            {
                loading &&
                <div className="row h-100">
                    <div className="col-12 h-100 d-flex flex-column justify-content-center align-items-center">
                        <img src={getDatalakeImageUrl("/loaders/RecommendationLoader.gif")} alt=""/>
                        <h3 className="mt-4">{ t("loading_message", { ns: "linked_entity/alice_relation_recommendation_dialog" }) }</h3>
                    </div>
                </div>
            }
            {
                !loading &&
                error.length > 0 &&
                <FailedToLoadError />
            }
            {
                !loading &&
                error.length === 0 &&
                <div className="row gy-2 gx-1">
                    {
                        recommendedRelations.length === 0 &&
                        <div>
                            { t("no_relations_message", { ns: "linked_entity/alice_relation_recommendation_dialog" }) }
                        </div>
                    }
                    {recommendedRelations.map((relation, index) => (
                        <RecommendedRelationCard
                            key={index}
                            selected={relation.checked}
                            fromEntityName={relation.from}
                            toEntityName={relation.to}
                            cardinality={relation.cardinality}
                            addReferenceAttribute={relation.showReferenceAttribute}
                            referenceAttribute={relation.referenceAttribute}
                            referenceAttributes={getSelectedAttributeNameOfEntity(relation.from)}
                            onToggle={() => toggleRelationChecked(index)}
                            onCardinalityChange={(event) => handleCardinalityChange(event, index)}
                            onAddReferenceAttributeChange={(event) => toggleShowReferenceAttribute(index)}
                            onReferenceAttributeChange={(event) => handleReferenceAttributeChange(event, index)}
                            onSwap={event => handleSwap(event, index)}
                        />
                    ))}
                </div>
            }
            {
                !loading &&
                recommendedRelations.length > 0 &&
                <DialogActionsBar layout="end">
                    <div className='d-flex align-items-center mb-4 me-3 pe-2'>
                        {
                            creatingRelations &&
                            <InnerLoader borderColor="green" />
                        }
                        <button className="btn" onClick={handleToggle}>
                            { t("buttons.cancel", { ns: "linked_entity/alice_relation_recommendation_dialog" }) }
                        </button>
                        <button className="btn btn-primary" disabled={isAddDisabled()} onClick={handleSubmit}>
                            { t("buttons.add", { ns: "linked_entity/alice_relation_recommendation_dialog" }) }
                        </button>
                    </div>
                </DialogActionsBar>
            }
        </Dialog>
    )
};


/*
<div key={index} className="col-4 bg-white px-1">
    <button
        className={`w-100 h-100 bg-white ${relation.checked ? "border-primary border-3" : " border border-secondary"}`}
        onClick={() => toggleRelationChecked(index)}
        disabled={relation.isStatic}
    >
        <div className={`h-100 d-flex flex-column align-items-center justify-content-center py-1 px-2`}>
            <div className="btn border-0 w-100">
                <SelectedCardinality
                    selected={relation.cardinality}
                    firstEntityName={relation.from}
                    secondEntityName={relation.to} />
            </div>
            <div>
                {
                    relation.cardinality === "1-*"
                    ?
                    <label
                        onClick={e => e.stopPropagation()}>
                        <Checkbox
                            value={relation.showReferenceAttribute}
                            checked={relation.showReferenceAttribute}
                            disabled={relation.isStatic}
                            onChange={(e) => {
                                e.syntheticEvent.stopPropagation();
                                e.nativeEvent.stopPropagation();
                                toggleShowReferenceAttribute(index)
                            }}
                            onClick={e => {
                                e.stopPropagation()
                            }} />
                        &nbsp;
                        Add Reference {ATTRIBUTE_LABEL}
                    </label>
                    :
                    <>&nbsp;</>
                }
                {
                    relation.showReferenceAttribute
                    ?
                    <DropDownList
                        data={getSelectedAttributeNameOfEntity(relation.from)}
                        value={relation.referenceAttribute}
                        disabled={relation.isStatic}
                        onChange={(e) => handleReferenceAttributeChange(e, index)}
                        onOpen={e => {
                            e.syntheticEvent.stopPropagation();
                        }}
                        onClose={e => {
                            e.syntheticEvent.stopPropagation();
                        }} />
                    :
                    <>&nbsp;</>
                }
            </div>
        </div>
    </button>
</div>
*/

export default AliceRelationRecommendationDialog;